import type {ReactElement} from 'react';
import React from 'react';
import type {InputSuggestionsDropdownProps, SuggestionItems} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {noop} from '@Utils/general.util';
import {SuggestionListItem} from '@Components/input-suggestions-dropdown/suggestion-list-item/suggestion-list-item';
import Divider from '@Components/divider';

export function InputSuggestionsDropdown({
  dropdownId,
  inputSelector,
  primarySuggestionsList = [],
  recentSuggestionsList = [],
  miscSuggestionsList = [],
  className = '',
  onSuggestionSelect = noop,
  popupClassName = '_full-width',
  popupAlignment = ALIGNMENT_TYPE.BOTTOM,
  popupPosition = DROPDOWN_POSITION.ABSOLUTE,
  inputValue = '',
  truncateText = false,
  showSuggestionAsTitleOnHover = false
}: InputSuggestionsDropdownProps): ReactElement {
  const getFilteredPrimarySuggestions = (): SuggestionItems => {
    return inputValue ? primarySuggestionsList.filter((item) => item.primaryText.toLowerCase().includes(inputValue.toLowerCase())) : primarySuggestionsList;
  };

  const getFilteredRecentSuggestions = (): SuggestionItems => {
    return inputValue ? recentSuggestionsList.filter((item) => item.primaryText.toLowerCase().includes(inputValue.toLowerCase())) : recentSuggestionsList;
  };

  const getFilteredMiscSuggestions = (): SuggestionItems => {
    return inputValue ? miscSuggestionsList.filter((item) => item.primaryText.toLowerCase().includes(inputValue.toLowerCase())) : miscSuggestionsList;
  };

  const filteredPrimarySuggestions = getFilteredPrimarySuggestions();
  const filteredRecentSuggestions = getFilteredRecentSuggestions();
  const filteredMiscSuggestions = getFilteredMiscSuggestions();

  const getSelector = (): ReactElement => {
    return <div onKeyDown={onKeyDown}>{inputSelector}</div>;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      e.currentTarget.click();
    }
  };

  const getListItemClasses = (): string => {
    if (filteredPrimarySuggestions.length + filteredRecentSuggestions.length + filteredMiscSuggestions.length > 1) {
      return 'spacing-m-b-2';
    }
    return '';
  };

  const getPopupListContent = (): ReactElement => {
    return (
      <div>
        {getPrimaryListContent()}
        {isPrimaryDividerNeeded() ? getDivider() : null}
        {getRecentListContent()}
        {isSecondaryDividerNeeded() ? getDivider() : null}
        {getMiscListContent()}
      </div>
    );
  };

  const getPrimaryListContent = (): ReactElement[] => {
    return filteredPrimarySuggestions.map((suggestion, index) => {
      const isLastElement = !isPrimaryDividerNeeded() && index === filteredPrimarySuggestions.length - 1;
      return (
        <SuggestionListItem
          key={`primary_list_${suggestion.primaryText}`}
          {...suggestion}
          onClick={onSuggestionSelect}
          className={isLastElement ? '' : getListItemClasses()}
          truncatePrimaryText={truncateText}
          showSuggestionAsTitleOnHover={showSuggestionAsTitleOnHover}
        />
      );
    });
  };

  const getRecentListContent = (): ReactElement[] => {
    return filteredRecentSuggestions.map((suggestion, index) => {
      const isLastElement = !isSecondaryDividerNeeded() && index === filteredRecentSuggestions.length - 1;
      return (
        <SuggestionListItem
          key={`recent_list_${suggestion.primaryText}`}
          {...suggestion}
          onClick={onSuggestionSelect}
          className={isLastElement ? '' : getListItemClasses()}
          truncatePrimaryText={truncateText}
          showSuggestionAsTitleOnHover={showSuggestionAsTitleOnHover}
        />
      );
    });
  };

  const getMiscListContent = (): ReactElement[] => {
    return filteredMiscSuggestions.map((suggestion, index) => {
      const isLastElement = index === filteredMiscSuggestions.length - 1;
      return (
        <SuggestionListItem
          key={`recent_list_${suggestion.primaryText}`}
          {...suggestion}
          onClick={onSuggestionSelect}
          className={isLastElement ? '' : getListItemClasses()}
          truncatePrimaryText={truncateText}
          showSuggestionAsTitleOnHover={showSuggestionAsTitleOnHover}
        />
      );
    });
  };

  const isPrimaryDividerNeeded = (): boolean => {
    return filteredPrimarySuggestions.length > 0 && filteredRecentSuggestions.length > 0;
  };

  const isSecondaryDividerNeeded = (): boolean => {
    return filteredRecentSuggestions.length > 0 && filteredMiscSuggestions.length > 0;
  };

  const getDivider = (): ReactElement => {
    return <Divider additionalClasses="spacing-m-b-2" />;
  };

  const shouldShowPopup = (): boolean => {
    return filteredPrimarySuggestions.length > 0 || filteredRecentSuggestions.length > 0 || filteredMiscSuggestions.length > 0;
  };

  return (
    <div className={`_full-width ${className}`}>
      <Dropdown
        id={dropdownId}
        selector={getSelector()}
        popup={getPopupListContent()}
        position={popupPosition}
        popupClassName={`radius-8 spacing-p-3 border-s-standard ${popupClassName} ${shouldShowPopup() ? '' : '_hidden'}`}
        alignment={popupAlignment}
        popUpHasCustomWidth
      />
    </div>
  );
}
